import { useQueries, useQuery } from '@tanstack/react-query';

import type { IRolesState, IRolesStates } from '../../../interface/state/IRole';
import { coreApi } from '../../../lib/api-client';
import { roleKeys } from './management-keys';

export interface RoleListParams {
	pageNumber?: number;
	pageSize?: number;
	name?: string;
	isAdministratorRole?: string;
	sortBy?: string;
	sortDirection?: string;
}

function buildQueryString(params: RoleListParams): string {
	const sp = new URLSearchParams();
	if (params.pageNumber) sp.set('PageNumber', String(params.pageNumber));
	if (params.pageSize) sp.set('PageSize', String(params.pageSize));
	if (params.name) sp.set('Name', params.name);
	if (params.isAdministratorRole) sp.set('IsAdministratorRole', params.isAdministratorRole);
	if (params.sortBy) sp.set('sortBy', params.sortBy);
	if (params.sortDirection) sp.set('sortDirection', params.sortDirection);
	const qs = sp.toString();
	return qs ? `?${qs}` : '';
}

/** F3.02 — paged role list. */
export function useRolesQuery(params: RoleListParams = {}) {
	return useQuery({
		queryKey: roleKeys.list(params as Record<string, unknown>),
		queryFn: async (): Promise<IRolesStates> => {
			const { data } = await coreApi.get(`/api/v1/Role${buildQueryString(params)}`);
			return data;
		},
	});
}

/** F3.02 — single role lookup by name. */
export function useRoleByNameQuery(name: string | undefined) {
	return useQuery({
		queryKey: roleKeys.byName(name || ''),
		queryFn: async (): Promise<IRolesState> => {
			const { data } = await coreApi.get(`/api/v1/Role/Name?Name=${encodeURIComponent(name!)}`);
			return data;
		},
		enabled: !!name,
	});
}

/** F3.02 — users in a role. */
export function useUsersInRoleQuery(roleName: string | undefined) {
	return useQuery({
		queryKey: roleKeys.usersInRole(roleName || ''),
		queryFn: async () => {
			const { data } = await coreApi.get(`/api/v1/Role/GetUsersInRole?role=${encodeURIComponent(roleName!)}`);
			return data;
		},
		enabled: !!roleName,
	});
}

/** F3.02 — action names assigned to a single role. Used in the role detail panel + Manage Actions. */
export function useRoleActionsForRoleQuery(roleName: string | undefined) {
	return useQuery({
		queryKey: roleKeys.actionsForRole(roleName || ''),
		queryFn: async (): Promise<{ data: string[] }> => {
			const { data } = await coreApi.get(`/api/v1/Action/getActionsForRole?roleName=${encodeURIComponent(roleName!)}`);
			return data;
		},
		enabled: !!roleName,
	});
}

/**
 * F3.10 — fetches the actions-for-role count for every role in `roleNames`
 * in parallel. Replaces the sequential waterfall in `hooks/role.tsx` that
 * fired N requests one after the other.
 *
 * Returns a map of `roleName -> assigned action count` derived from
 * individual `useRoleActionsForRoleQuery` queries via `useQueries`. The
 * cache keying matches the per-role query so opening the role detail
 * panel after a count is computed is an instant cache hit.
 *
 * Note: this fires N parallel requests but they go through the existing
 * `coreApi` axios instance, so cancel-token semantics are honored per call.
 * The previous Redux-action approach was forced to be sequential to avoid
 * cancel-token collisions on the same action type — TanStack Query has no
 * such constraint.
 */
export function useRoleActionCounts(roleNames: string[]) {
	const results = useQueries({
		queries: roleNames.map((name) => ({
			queryKey: roleKeys.actionsForRole(name),
			queryFn: async (): Promise<{ data: string[] }> => {
				const { data } = await coreApi.get(`/api/v1/Action/getActionsForRole?roleName=${encodeURIComponent(name)}`);
				return data;
			},
			staleTime: 5 * 60 * 1000,
		})),
	});

	const isLoading = results.some((r) => r.isLoading);
	const countMap = roleNames.reduce<Record<string, number>>((acc, name, idx) => {
		const list = results[idx]?.data?.data;
		if (Array.isArray(list)) {
			acc[name] = list.length;
		}
		return acc;
	}, {});

	return { isLoading, countMap, results };
}

/**
 * F5.04 — fans out the same `getActionsForRole` calls as `useRoleActionCounts`
 * (and shares the cache), then derives the list of role names whose assigned
 * actions include `actionName`. Used by the Role Action detail panel's
 * "Used by N roles" section.
 *
 * Because the cache key matches `useRoleActionCounts`, opening the detail
 * panel after the Roles dashboard has computed counts is an instant cache
 * hit — no network round trips.
 */
export function useRolesUsingAction(actionName: string | undefined, roleNames: string[]) {
	const results = useQueries({
		queries: roleNames.map((name) => ({
			queryKey: roleKeys.actionsForRole(name),
			queryFn: async (): Promise<{ data: string[] }> => {
				const { data } = await coreApi.get(`/api/v1/Action/getActionsForRole?roleName=${encodeURIComponent(name)}`);
				return data;
			},
			staleTime: 5 * 60 * 1000,
			enabled: !!actionName,
		})),
	});

	const isLoading = results.some((r) => r.isLoading);
	const rolesUsing = actionName
		? roleNames.filter((_, idx) => {
				const list = results[idx]?.data?.data;
				return Array.isArray(list) && list.includes(actionName);
			})
		: [];

	return { isLoading, rolesUsing };
}
